import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import OverviewView from "./components/OverviewView";
import EnhancedTestsView from "./components/EnhancedTestsView";
import DeliveryView from "./components/DeliveryView";
import ReportsView from "./components/ReportsView";
import BottomNavigation from "./components/BottomNavigation";
import NotificationsView from "./components/NotificationsView";
import ProfileView from "./components/ProfileView";
import SettingsView from "./components/SettingsView";
import AnalyticsView from "./components/AnalyticsView";
import HelpView from "./components/HelpView";
import Toast from "./components/Toast";
import OrderManagementView from "./components/OrderManagementView";
import GlobalNotificationBar from "./components/GlobalNotificationBar";
import TopNavigationBar from "./components/TopNavigationBar";
import { DriverAppNew } from "./components/DriverAppNew";

/**
 * RMC Quality Intelligence Application
 * Premium $100M industrial SaaS interface for Ready-Mix Concrete quality testing
 * Features comprehensive testing workflows, PDF report generation, and real-time analytics
 * @version 1.0.5
 */
export default function App() {
  const [activeTab, setActiveTab] = useState("overview");
  const [activeMoreOption, setActiveMoreOption] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "info" | "warning" | "error" } | null>(null);
  const [showOrderManagement, setShowOrderManagement] = useState(false);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [showDriverApp, setShowDriverApp] = useState(false);
  const [batchData, setBatchData] = useState({
    batchId: "RMC-2024-12-2847",
    grade: "M30",
    plant: "Central Mixing Plant",
    status: "optimal" as const,
  });

  // Notifications state
  const [notifications, setNotifications] = useState([
    {
      id: "notif-1",
      type: "test-due" as const,
      title: "7-Day Test Due",
      message: "Cube strength test is due today for batch RMC-2024-12-2847. Navigate to Tests tab to perform testing.",
      time: "2 hours ago",
      isRead: false,
      action: {
        label: "Add Test",
        onClick: () => setActiveTab("tests"),
      },
    },
    {
      id: "notif-2",
      type: "reminder" as const,
      title: "Slump Test Reminder",
      message: "Scheduled slump test reminder for fresh concrete batch. Ensure equipment is calibrated.",
      time: "5 hours ago",
      isRead: false,
      action: {
        label: "View Details",
        onClick: () => setActiveTab("tests"),
      },
    },
    {
      id: "notif-3",
      type: "quality-alert" as const,
      title: "Quality Check Required",
      message: "Mix design verification needed before next batch production. Review material compliance.",
      time: "1 day ago",
      isRead: true,
    },
  ]);

  const handleMarkAsRead = (notificationId: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
    );
    showToast("Notification marked as read", "success");
  };

  const handleDeleteNotification = (notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    showToast("Notification deleted", "success");
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const showToast = (message: string, type: "success" | "info" | "warning" | "error" = "success") => {
    setToast({ message, type });
  };

  const handleDownload = () => {
    showToast("Report downloaded successfully", "success");
    console.log("Downloading report...");
  };

  const handleShare = () => {
    showToast("Report shared via email", "success");
    console.log("Sharing report...");
  };

  const handleSignalClick = (signalId: string) => {
    console.log("Signal clicked:", signalId);
    showToast(`Viewing ${signalId} test details`, "info");
    setActiveTab("tests");
  };

  const handleRefreshData = () => {
    showToast("Refreshing batch data...", "info");
    setTimeout(() => {
      showToast("Data updated successfully", "success");
    }, 1500);
  };

  const handleExportData = () => {
    showToast("Exporting batch data...", "info");
    setTimeout(() => {
      showToast("Data exported successfully", "success");
    }, 1000);
  };

  const handleViewHistory = () => {
    showToast("Loading batch history...", "info");
    setShowOrderManagement(true);
  };

  // Handle test completion and auto-generate report
  const handleTestComplete = (testData: {
    testId: string;
    testName: string;
    batchId: string;
    grade: string;
    date: string;
    values: any;
  }) => {
    const reportId = `RPT-${Date.now()}`;
    
    showToast(`${testData.testName} completed! Report generated: ${reportId}`, "success");
    
    // Navigate to reports after 1 second
    setTimeout(() => {
      setActiveTab("reports");
    }, 1500);
  };

  // Complete test data for the batch
  const testData = {
    fresh: {
      slump: 85,
      temperature: 28,
      density: 2450,
    },
    strength: {
      day7: { value: 24.2, weight: 8250, date: "Dec 30, 2024" },
      day14: { value: 29.8, weight: 8240, date: "Jan 06, 2025" },
      day28: { value: 33.8, weight: 8235, date: "Jan 20, 2025" },
    },
    mixDesign: {
      cement: 380,
      coarseAggregate: 1165,
      fineAggregate: 680,
      water: 171,
      admixture: 3.8,
      wcRatio: 0.45,
    },
    casting: {
      date: "Dec 23, 2024",
      time: "09:42 AM",
      location: "Central Mixing Plant",
      weather: "Clear, 28°C",
    },
  };

  // Mock data
  const qualitySignals = [
    {
      id: "slump",
      title: "Slump",
      value: "85",
      unit: "mm",
      confidence: "high" as const,
      trend: "stable" as const,
      onClick: () => handleSignalClick("slump"),
    },
    {
      id: "material",
      title: "Material",
      value: "A+",
      confidence: "high" as const,
      trend: "up" as const,
      onClick: () => handleSignalClick("material"),
    },
    {
      id: "strength",
      title: "Strength",
      value: "33.8",
      unit: "MPa",
      confidence: "high" as const,
      trend: "up" as const,
      onClick: () => handleSignalClick("strength"),
    },
  ];

  const materialLayers = [
    {
      id: "cement",
      name: "Cement",
      quality: "OPC 53 Grade",
      compliance: "compliant" as const,
      imageUrl:
        "https://images.unsplash.com/photo-1639633901978-772ceb0b1a4a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMG1peCUyMGRlc2lnbiUyMG1hdGVyaWFsc3xlbnwxfHx8fDE3NjY0ODM0MDB8MA&ixlib=rb-4.1.0&q=80&w=1080",
    },
    {
      id: "aggregate",
      name: "Coarse Aggregate",
      quality: "20mm nominal size",
      compliance: "compliant" as const,
      imageUrl:
        "https://images.unsplash.com/photo-1639633901978-772ceb0b1a4a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMG1peCUyMGRlc2lnbiUyMG1hdGVyaWFsc3xlbnwxfHx8fDE3NjY0ODM0MDB8MA&ixlib=rb-4.1.0&q=80&w=1080",
    },
    {
      id: "sand",
      name: "Fine Aggregate",
      quality: "Zone II conforming",
      compliance: "compliant" as const,
      imageUrl:
        "https://images.unsplash.com/photo-1639633901978-772ceb0b1a4a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMG1peCUyMGRlc2lnbiUyMG1hdGVyaWFsc3xlbnwxfHx8fDE3NjY0ODM0MDB8MA&ixlib=rb-4.1.0&q=80&w=1080",
    },
  ];

  const cubeTests = [
    { day: 7, strength: 24.2, status: "completed" as const },
    { day: 14, strength: 29.8, status: "completed" as const },
    { day: 28, strength: 33.8, status: "completed" as const },
  ];

  const qualityInsights = [
    {
      id: "1",
      text: "Strength exceeds target by 6.2%",
      type: "positive" as const,
    },
    {
      id: "2",
      text: "Slump consistency within optimal range",
      type: "positive" as const,
    },
    {
      id: "3",
      text: "All material compliance verified",
      type: "positive" as const,
    },
  ];

  const batchComparison = {
    currentBatch: {
      id: "RMC-2024-12-2847",
      date: "Dec 23, 2024",
      strength: 33.8,
      status: "optimal" as const,
    },
    recentBatches: [
      { id: "RMC-2846", date: "Dec 22", strength: 32.1, status: "optimal" as const },
      { id: "RMC-2845", date: "Dec 21", strength: 31.8, status: "optimal" as const },
      { id: "RMC-2844", date: "Dec 20", strength: 33.2, status: "optimal" as const },
      { id: "RMC-2843", date: "Dec 19", strength: 30.9, status: "acceptable" as const },
      { id: "RMC-2842", date: "Dec 18", strength: 32.5, status: "optimal" as const },
      { id: "RMC-2841", date: "Dec 17", strength: 31.4, status: "optimal" as const },
      { id: "RMC-2840", date: "Dec 16", strength: 33.0, status: "optimal" as const },
      { id: "RMC-2839", date: "Dec 15", strength: 32.8, status: "optimal" as const },
      { id: "RMC-2838", date: "Dec 14", strength: 31.2, status: "optimal" as const },
      { id: "RMC-2837", date: "Dec 13", strength: 32.6, status: "optimal" as const },
    ],
  };

  const mixComponents = [
    { id: "cement", name: "Cement (OPC 53)", quantity: 380, unit: "kg/m³", percentage: 16.8 },
    { id: "coarse", name: "Coarse Aggregate (20mm)", quantity: 1165, unit: "kg/m³", percentage: 51.5 },
    { id: "fine", name: "Fine Aggregate", quantity: 680, unit: "kg/m³", percentage: 30.1 },
    { id: "water", name: "Water", quantity: 171, unit: "liters", percentage: 7.6 },
    { id: "admixture", name: "Admixture", quantity: 3.8, unit: "liters", percentage: 0.2 },
  ];

  const deliveries = [
    {
      batchId: "RMC-2024-12-2847",
      vehicleNumber: "TN-01-AB-1234",
      driverName: "Rajesh Kumar",
      departureTime: "09:15 AM",
      arrivalTime: "10:30 AM",
      distance: "12.4 km",
      temperature: 28,
      status: "in-transit" as const,
      imageUrl:
        "https://images.unsplash.com/photo-1760045788252-d8d386ea1d12?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwZGVsaXZlcnklMjB0cnVja3xlbnwxfHx8fDE3NjY0ODM0MDB8MA&ixlib=rb-4.1.0&q=80&w=1080",
    },
    {
      batchId: "RMC-2024-12-2846",
      vehicleNumber: "TN-01-AB-5678",
      driverName: "Suresh Babu",
      departureTime: "08:45 AM",
      arrivalTime: "09:50 AM",
      distance: "8.2 km",
      temperature: 27,
      status: "delivered" as const,
      imageUrl:
        "https://images.unsplash.com/photo-1760045788252-d8d386ea1d12?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwZGVsaXZlcnklMjB0cnVja3xlbnwxfHx8fDE3NjY0ODM0MDB8MA&ixlib=rb-4.1.0&q=80&w=1080",
    },
    {
      batchId: "RMC-2024-12-2845",
      vehicleNumber: "TN-01-AB-9012",
      driverName: "Murugan S",
      departureTime: "11:00 AM",
      arrivalTime: "12:15 PM",
      distance: "15.7 km",
      temperature: 29,
      status: "pending" as const,
      imageUrl:
        "https://images.unsplash.com/photo-1760045788252-d8d386ea1d12?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwZGVsaXZlcnklMjB0cnVja3xlbnwxfHx8fDE3NjY0ODM0MDB8MA&ixlib=rb-4.1.0&q=80&w=1080",
    },
  ];

  const slumpTestImage = "https://images.unsplash.com/photo-1652111908774-696ceb2e5139?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMHNsdW1wJTIwdGVzdHxlbnwxfHx8fDE3NjY0ODI0OTZ8MA&ixlib=rb-4.1.0&q=80&w=1080";
  const cubeTestBeforeImage = "https://images.unsplash.com/photo-1581631059653-966a63132741?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMGN1YmUlMjBzYW1wbGV8ZW58MXx8fHwxNzY2NDgyNDk2fDA&ixlib=rb-4.1.0&q=80&w=1080";
  const cubeTestAfterImage = "https://images.unsplash.com/photo-1576669801343-117bb4054118?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsYWJvcmF0b3J5JTIwdGVzdGluZyUyMGVxdWlwbWVudHxlbnwxfHx8fDE3NjYzODcwMjV8MA&ixlib=rb-4.1.0&q=80&w=1080";

  return (
    <>
      {showDriverApp ? (
        <DriverAppNew onClose={() => setShowDriverApp(false)} />
      ) : (
      <div className="min-h-screen bg-[#F9FAFB] w-full overflow-x-hidden">
        {/* Main Content Container - Responsive Max Width */}
        <div className="w-full max-w-md sm:max-w-xl md:max-w-2xl lg:max-w-4xl xl:max-w-6xl mx-auto">
          {/* Global Top Navigation Bar - Sticky on All Pages */}
          {!showOrderManagement && (
            <TopNavigationBar
              notificationCount={unreadCount}
              onNotificationClick={() => setShowNotificationPanel(true)}
              showBackButton={activeMoreOption !== null}
              onBackClick={() => setActiveMoreOption(null)}
              title={
                activeMoreOption 
                  ? activeMoreOption 
                  : activeTab === "overview" 
                  ? undefined 
                  : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)
              }
            />
          )}
          
          {/* Main Content */}
          {!showOrderManagement && (
            <>
              {activeTab === "overview" && activeMoreOption === null && (
                <OverviewView
                  grade={batchData.grade}
                  batchId={batchData.batchId}
                  plant={batchData.plant}
                  timestamp="Dec 23, 2024 - 09:42 AM"
                  status={batchData.status}
                  qualitySignals={qualitySignals}
                  slumpValue={85}
                  slumpUnit="mm"
                  slumpRange="75-100 mm"
                  slumpStatus="optimal"
                  slumpImage={slumpTestImage}
                  materialLayers={materialLayers}
                  cubeTests={cubeTests}
                  currentStrength={33.8}
                  requiredStrength={30}
                  strengthUnit="MPa"
                  beforeImage={cubeTestBeforeImage}
                  afterImage={cubeTestAfterImage}
                  qualityInsights={qualityInsights}
                  batchComparison={batchComparison}
                  onNavigateToTests={() => setActiveTab("tests")}
                  onNavigateToAnalytics={() => {
                    setActiveTab("more");
                    setActiveMoreOption("Analytics");
                  }}
                  notifications={notifications}
                  onMarkAsRead={handleMarkAsRead}
                  onDeleteNotification={handleDeleteNotification}
                />
              )}

              {activeTab === "tests" && (
                <EnhancedTestsView
                  batchId="RMC-2024-12-2847"
                  castingDate="Dec 23, 2024"
                  grade="M30"
                  onNavigateToReports={() => setActiveTab("reports")}
                  onTestComplete={handleTestComplete}
                />
              )}

              {activeTab === "delivery" && <DeliveryView deliveries={deliveries} />}

              {activeTab === "reports" && (
                <ReportsView
                  onNavigateToTests={() => setActiveTab("tests")}
                />
              )}

              {activeTab === "more" && (
                <>
                  {!activeMoreOption && (
                    <div className="px-5 pt-6 pb-24" style={{ backgroundColor: "#F7F8FA", minHeight: "100vh" }}>
                      <h1
                        style={{
                          fontSize: "24px",
                          letterSpacing: "-0.02em",
                          fontWeight: "700",
                          color: "#0B1220",
                          marginBottom: "24px",
                        }}
                      >
                        More
                      </h1>
                      <div className="space-y-2">
                        {[
                          { 
                            title: "Settings", 
                            desc: "App preferences",
                          },
                          { 
                            title: "Analytics", 
                            desc: "Performance insights",
                          },
                          { 
                            title: "Profile", 
                            desc: "Account settings",
                          },
                          { 
                            title: "Notifications", 
                            desc: "Alert preferences",
                          },
                          { 
                            title: "Help & Support", 
                            desc: "Get assistance",
                          },
                        ].map((item, i) => (
                          <button
                            key={i}
                            className="w-full bg-white p-4 rounded-2xl text-left flex items-center justify-between"
                            style={{
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                            }}
                            onClick={() => {
                              if (item.title === "Notifications") {
                                setShowNotificationPanel(true);
                              } else {
                                setActiveMoreOption(item.title);
                              }
                            }}
                          >
                            <div className="flex-1">
                              <div
                                style={{ fontSize: "14px", fontWeight: "600", color: "#0B1220" }}
                              >
                                {item.title}
                              </div>
                              <div
                                style={{ fontSize: "12px", fontWeight: "500", color: "#5B6475" }}
                              >
                                {item.desc}
                              </div>
                            </div>
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 18 18"
                              fill="none"
                            >
                              <path
                                d="M7 4l5 5-5 5"
                                stroke="#5B6475"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        ))}
                        
                        {/* Driver App Demo Button */}
                        <button
                          className="w-full p-4 rounded-2xl text-left flex items-center justify-between"
                          style={{
                            background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                            boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                          }}
                          onClick={() => window.location.href = 'https://pouch-oculus-98573656.figma.site'}
                        >
                          <div className="flex-1">
                            <div
                              style={{ fontSize: "14px", fontWeight: "700", color: "#FFFFFF" }}
                            >
                              🚛 Driver Dashboard Demo
                            </div>
                            <div
                              style={{ fontSize: "12px", fontWeight: "500", color: "rgba(255, 255, 255, 0.9)" }}
                            >
                              View redesigned driver operations
                            </div>
                          </div>
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                          >
                            <path
                              d="M7 4l5 5-5 5"
                              stroke="#FFFFFF"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Render more options view based on active more option */}
                  {activeMoreOption === "Settings" && (
                    <SettingsView 
                      notificationCount={unreadCount} 
                      onNotificationClick={() => {
                        setActiveMoreOption("Notifications");
                      }} 
                    />
                  )}
                  {activeMoreOption === "Analytics" && <AnalyticsView />}
                  {activeMoreOption === "Profile" && <ProfileView />}
                  {activeMoreOption === "Notifications" && (
                    <NotificationsView
                      notifications={notifications}
                      onMarkAsRead={handleMarkAsRead}
                      onDelete={handleDeleteNotification}
                      unreadCount={unreadCount}
                    />
                  )}
                  {activeMoreOption === "Help & Support" && <HelpView />}
                </>
              )}

              {/* Bottom Navigation */}
              <BottomNavigation 
                activeTab={activeTab} 
                onTabChange={(tab) => {
                  setActiveTab(tab);
                  setActiveMoreOption(null); // Reset more option when changing tabs
                }}
                notificationCount={unreadCount}
              />
            </>
          )}
          {showOrderManagement && <OrderManagementView onClose={() => setShowOrderManagement(false)} />}
        </div>
      </div>
      )}
      
      {/* Global Notification Bottom Sheet */}
      <AnimatePresence>
        {showNotificationPanel && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              onClick={() => setShowNotificationPanel(false)}
              className="fixed inset-0 z-[60]"
              style={{ 
                backgroundColor: "rgba(10, 15, 28, 0.6)",
                backdropFilter: "blur(12px)",
              }}
            />

            {/* Notification Bottom Sheet - Premium Redesign */}
            <motion.div
              initial={{ opacity: 0, y: "100%", scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: "100%", scale: 0.95 }}
              transition={{ 
                type: "spring", 
                damping: 32, 
                stiffness: 380,
                mass: 0.8,
              }}
              className="fixed bottom-0 left-0 right-0 z-[70] overflow-hidden"
              style={{
                maxWidth: "640px",
                margin: "0 auto",
                maxHeight: "82vh",
              }}
            >
              {/* Main Container with Premium Glassmorphic Background */}
              <div
                className="relative"
                style={{
                  background: "linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)",
                  borderTopLeftRadius: "32px",
                  borderTopRightRadius: "32px",
                  boxShadow: `
                    0 -2px 4px rgba(10, 15, 28, 0.03),
                    0 -8px 16px rgba(10, 15, 28, 0.08),
                    0 -16px 48px rgba(10, 15, 28, 0.12)
                  `,
                }}
              >
                {/* Drag Handle */}
                <div className="flex justify-center pt-5 pb-3">
                  <div
                    className="w-12 h-1.5 rounded-full"
                    style={{ 
                      background: "linear-gradient(90deg, rgba(27, 42, 91, 0.15) 0%, rgba(27, 42, 91, 0.25) 50%, rgba(27, 42, 91, 0.15) 100%)",
                    }}
                  />
                </div>

                {/* Header Section */}
                <div className="px-6 pt-2 pb-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h2
                        className="text-[#0A0F1C] mb-1"
                        style={{
                          fontSize: "28px",
                          fontWeight: "700",
                          letterSpacing: "-0.025em",
                          lineHeight: "1.2",
                        }}
                      >
                        Notifications
                      </h2>
                      <p
                        className="text-[#717182]"
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {unreadCount > 0 
                          ? `${unreadCount} new update${unreadCount !== 1 ? 's' : ''} for you`
                          : "You're all caught up"
                        }
                      </p>
                    </div>
                    
                    {/* Unread Badge */}
                    {unreadCount > 0 && (
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 400 }}
                        className="flex items-center justify-center px-3 py-1.5 rounded-xl"
                        style={{
                          background: "linear-gradient(135deg, rgba(220, 38, 38, 0.08) 0%, rgba(220, 38, 38, 0.12) 100%)",
                          boxShadow: "0 1px 3px rgba(220, 38, 38, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                        }}
                      >
                        <div
                          className="w-2 h-2 rounded-full mr-2"
                          style={{
                            backgroundColor: "#DC2626",
                            boxShadow: "0 0 8px rgba(220, 38, 38, 0.5)",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color: "#DC2626",
                            letterSpacing: "0.02em",
                          }}
                        >
                          {unreadCount} NEW
                        </span>
                      </motion.div>
                    )}
                  </div>
                </div>

              {/* Notifications List - Redesigned */}
              <div className="px-6 overflow-y-auto pb-2" style={{ maxHeight: "calc(75vh - 200px)" }}>
                {notifications.length === 0 ? (
                  <div className="text-center py-16">
                    <div 
                      className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                      style={{
                        backgroundColor: "#F9FAFB",
                      }}
                    >
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <path
                          d="M16 28C22.6274 28 28 22.6274 28 16C28 9.37258 22.6274 4 16 4C9.37258 4 4 9.37258 4 16C4 22.6274 9.37258 28 16 28Z"
                          stroke="#717182"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 12H12.02M20 12H20.02M12 20C12 20 14 22 16 22C18 22 20 20 20 20"
                          stroke="#717182"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="text-[#0A0F1C] mb-1" style={{ fontSize: "15px", fontWeight: "600" }}>
                      No notifications
                    </div>
                    <div className="text-[#717182]" style={{ fontSize: "13px" }}>
                      You're all caught up!
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {notifications.slice(0, 3).map((notif, index) => (
                      <motion.div
                        key={notif.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.3 }}
                        className="rounded-2xl p-4 backdrop-blur-xl"
                        style={{
                          backgroundColor: notif.isRead 
                            ? "rgba(255, 255, 255, 0.5)" 
                            : "rgba(255, 255, 255, 0.9)",
                          border: notif.isRead
                            ? "1px solid rgba(0, 0, 0, 0.04)"
                            : "1px solid rgba(27, 42, 91, 0.08)",
                          boxShadow: notif.isRead
                            ? "0 1px 2px rgba(0, 0, 0, 0.03)"
                            : "0 2px 8px rgba(27, 42, 91, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)",
                        }}
                      >
                        <div className="flex items-start gap-3">
                          {/* Glassmorphic Status Indicator */}
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 backdrop-blur-xl"
                            style={{
                              backgroundColor: 
                                notif.type === "test-due"
                                  ? "rgba(52, 211, 153, 0.1)"
                                  : notif.type === "reminder"
                                  ? "rgba(16, 185, 129, 0.1)"
                                  : "rgba(110, 231, 183, 0.1)",
                              border:
                                notif.type === "test-due"
                                  ? "1px solid rgba(52, 211, 153, 0.2)"
                                  : notif.type === "reminder"
                                  ? "1px solid rgba(16, 185, 129, 0.2)"
                                  : "1px solid rgba(110, 231, 183, 0.2)",
                            }}
                          >
                            <div
                              className="w-2.5 h-2.5 rounded-full"
                              style={{
                                backgroundColor:
                                  notif.type === "test-due"
                                    ? "#34D399"
                                    : notif.type === "reminder"
                                    ? "#10B981"
                                    : "#6EE7B7",
                              }}
                            />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div
                              className="text-[#0A0F1C] mb-1"
                              style={{
                                fontSize: "14px",
                                fontWeight: "600",
                                lineHeight: "1.4",
                              }}
                            >
                              {notif.title}
                            </div>
                            <div
                              className="text-[#717182] mb-2"
                              style={{
                                fontSize: "13px",
                                lineHeight: "1.4",
                              }}
                            >
                              {notif.message}
                            </div>

                            {/* Time with subtle styling */}
                            <div className="flex items-center gap-1.5 text-[#717182]">
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-60">
                                <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2" />
                                <path d="M6 3V6L8 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                              </svg>
                              <span style={{ fontSize: "11px", fontWeight: "500" }}>
                                {notif.time}
                              </span>
                            </div>

                            {/* Action button if present */}
                            {notif.action && (
                              <button
                                onClick={() => {
                                  notif.action?.onClick();
                                  setShowNotificationPanel(false);
                                }}
                                className="mt-3 px-4 py-2 rounded-xl backdrop-blur-xl"
                                style={{
                                  backgroundColor: "#3B82F6",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  color: "white",
                                  boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
                                }}
                              >
                                {notif.action.label}
                              </button>
                            )}
                          </div>

                          {/* Action buttons */}
                          <div className="flex flex-col gap-2 flex-shrink-0">
                            {!notif.isRead && (
                              <button
                                onClick={() => handleMarkAsRead(notif.id)}
                                className="w-8 h-8 rounded-lg backdrop-blur-xl flex items-center justify-center transition-all"
                                style={{
                                  backgroundColor: "rgba(27, 42, 91, 0.06)",
                                  border: "1px solid rgba(27, 42, 91, 0.1)",
                                }}
                                title="Mark as read"
                              >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                  <path
                                    d="M11 4L5.5 9.5L3 7"
                                    stroke="#1B2A5B"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteNotification(notif.id)}
                              className="w-8 h-8 rounded-lg backdrop-blur-xl flex items-center justify-center transition-all"
                              style={{
                                backgroundColor: "rgba(220, 38, 38, 0.06)",
                                border: "1px solid rgba(220, 38, 38, 0.1)",
                              }}
                              title="Delete"
                            >
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path
                                  d="M3.5 3.5l7 7M10.5 3.5l-7 7"
                                  stroke="#DC2626"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bottom Actions - Redesigned */}
              <div 
                className="px-6 py-4 backdrop-blur-xl"
                style={{
                  backgroundColor: "rgba(249, 250, 251, 0.8)",
                  borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                }}
              >
                <div className="flex gap-3">
                  {notifications.length > 3 && (
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={() => {
                        setShowNotificationPanel(false);
                        setActiveTab("more");
                        setActiveMoreOption("Notifications");
                      }}
                      className="flex-1 py-3 rounded-xl backdrop-blur-xl"
                      style={{
                        backgroundColor: "#3B82F6",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "white",
                        boxShadow: "0 2px 12px rgba(59, 130, 246, 0.3), 0 1px 3px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      View All ({notifications.length})
                    </motion.button>
                  )}
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setShowNotificationPanel(false)}
                    className="py-3 rounded-xl backdrop-blur-xl"
                    style={{
                      backgroundColor: "rgba(113, 113, 130, 0.1)",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#717182",
                      border: "1px solid rgba(113, 113, 130, 0.2)",
                      minWidth: notifications.length > 3 ? "auto" : "100%",
                      paddingLeft: notifications.length > 3 ? "24px" : "0",
                      paddingRight: notifications.length > 3 ? "24px" : "0",
                    }}
                  >
                    Close
                  </motion.button>
                </div>
              </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </AnimatePresence>
      <GlobalNotificationBar 
        notifications={notifications}
        onMarkAsRead={handleMarkAsRead}
        onDeleteNotification={handleDeleteNotification}
      />
    </>
  );
}